import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/react-router';
import { FiCopy } from 'react-icons/fi';

import { client } from '../../../client';
import DashboardLayout from '../../layout/DashboardLayout';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Label, Select, Textarea } from '../../../components/ui';
import { BIRTHDAY_DEFAULT_VALUES, BIRTHDAY_FIXED_MUSIC, BIRTHDAY_THEME_OPTIONS } from '../../../birthday/constants';
import './index.css';

const BirthdayCreatePage = () => {
  const navigate = useNavigate();
  const { isLoaded, userId } = useAuth();
  const [values, setValues] = useState(BIRTHDAY_DEFAULT_VALUES);
  const [loading, setLoading] = useState(false);
  const [createdId, setCreatedId] = useState('');
  const [copied, setCopied] = useState(false);

  const publicLink = useMemo(
    () => (createdId ? `${window.location.origin}/birthday/${createdId}` : ''),
    [createdId]
  );

  if (!isLoaded || !userId) {
    navigate('/');
    return null;
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const canSubmit = values.recipientName && values.senderName && values.birthdayDate && values.headline && values.message;

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    setLoading(true);
    try {
      await client.createIfNotExists({ _id: userId, _type: 'user', userId });
      const birthdayDoc = {
        _type: 'birthdayPage',
        recipientName: values.recipientName,
        senderName: values.senderName,
        birthdayDate: new Date(`${values.birthdayDate}T00:00:00`).toISOString(),
        headline: values.headline,
        message: values.message,
        closingMessage: values.closingMessage,
        musicUrl: BIRTHDAY_FIXED_MUSIC,
        theme: values.theme,
        status: 'published',
        user: { _type: 'reference', _ref: userId },
      };
      const created = await client.create(birthdayDoc);
      setCreatedId(created._id);
    } catch (error) {
      console.error('Failed to create birthday page:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!publicLink) {
      return;
    }
    await navigator.clipboard.writeText(publicLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <DashboardLayout title="Create Birthday Page" subtitle="Make a personalized birthday page and share it instantly.">
      <Card className="birthday-create-card">
        <CardHeader>
          <CardTitle>Birthday Details</CardTitle>
          <CardDescription>Everything here is editable and shown on the public birthday page.</CardDescription>
        </CardHeader>
        <CardContent>
          {createdId ? (
            <div className="birthday-created-state">
              <h3>Birthday page created successfully</h3>
              <p className="ui-muted">Share this link with your friends and family:</p>
              <div className="birthday-created-actions">
                <a className="ui-link" href={publicLink} target="_blank" rel="noreferrer">
                  {publicLink}
                </a>
                <Button type="button" variant="secondary" onClick={handleCopyLink}>
                  <FiCopy />
                  {copied ? 'Copied' : 'Copy link'}
                </Button>
                <Button type="button" onClick={() => navigate('/dashboard/birthday')}>
                  Go to Birthday Pages
                </Button>
              </div>
            </div>
          ) : (
            <div className="birthday-create-grid">
              <div>
                <Label htmlFor="recipientName">Recipient Name</Label>
                <Input id="recipientName" name="recipientName" value={values.recipientName} onChange={handleChange} />
              </div>
              <div>
                <Label htmlFor="senderName">Sender Name</Label>
                <Input id="senderName" name="senderName" value={values.senderName} onChange={handleChange} />
              </div>
              <div>
                <Label htmlFor="birthdayDate">Birthday Date</Label>
                <Input id="birthdayDate" type="date" name="birthdayDate" value={values.birthdayDate} onChange={handleChange} />
              </div>
              <div>
                <Label htmlFor="theme">Theme</Label>
                <Select id="theme" name="theme" value={values.theme} onChange={handleChange}>
                  {BIRTHDAY_THEME_OPTIONS.map((themeOption) => (
                    <option key={themeOption.id} value={themeOption.id}>
                      {themeOption.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="headline">Headline</Label>
                <Input id="headline" name="headline" value={values.headline} onChange={handleChange} />
              </div>
              <div className="birthday-create-full">
                <Label htmlFor="message">Main Message</Label>
                <Textarea id="message" name="message" value={values.message} onChange={handleChange} />
              </div>
              <div className="birthday-create-full">
                <Label htmlFor="closingMessage">Closing Message (optional)</Label>
                <Textarea id="closingMessage" name="closingMessage" value={values.closingMessage} onChange={handleChange} />
              </div>
              <div className="birthday-create-full birthday-create-submit">
                <Button type="button" onClick={handleSubmit} disabled={!canSubmit || loading}>
                  {loading ? 'Creating...' : 'Create Birthday Page'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default BirthdayCreatePage;
